<template>
  <div
    data-test="notification-item"
    class="flex cursor-pointer items-center gap-3 px-3 py-2.5"
    :class="itemClasses"
    @click="handleClick"
  >
    <div
      class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
      :class="[iconColor, { border: !isInProgress }]"
    >
      <component :is="typeComponents.icon" v-if="typeComponents" class="h-5 w-5" />
      <SpinnerComp
        v-if="isInProgress || isDownloading"
        data-test="notification-progress-spinner"
        class="absolute inset-0 ml-0! h-10!"
      />
    </div>
    <div class="min-w-0 flex-1">
      <div
        v-if="variant === NOTIFICATION_ITEM_VARIANT.list"
        class="float-right flex items-start gap-2 pl-3"
      >
        <span class="text-xs whitespace-nowrap text-gray-400">{{ notification.time }}</span>
        <span
          class="mt-1 h-2 w-2 shrink-0 rounded-full"
          :class="notification.unread ? 'bg-red-500' : 'bg-transparent'"
        />
      </div>
      <component
        :is="typeComponents.content"
        v-if="typeComponents"
        :notification="notification"
        :is-downloading="isDownloading"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import DocumentDownloadIcon from '@/assets/untitled-ui/file-download-03.svg'
import RefreshIcon from '@/assets/untitled-ui/refresh-ccw-01.svg'
import { ChatAltIcon } from '@heroicons/vue/outline'
import CommentMentionContent from '@/components/notifications/content/CommentMentionContent.vue'
import FileExportContent from '@/components/notifications/content/FileExportContent.vue'
import ImportContent from '@/components/notifications/content/ImportContent.vue'
import SpinnerComp from '@/components/SpinnerComp.vue'
import {
  NOTIFICATION_ITEM_VARIANT,
  NOTIFICATION_STATUS,
  NOTIFICATION_TYPE
} from '@/lib/NotificationConstants'

const FILE_EXPORT_COMPONENTS = Object.freeze({
  icon: DocumentDownloadIcon,
  content: FileExportContent
})

const COMMENT_COMPONENTS = Object.freeze({
  icon: ChatAltIcon,
  content: CommentMentionContent
})

const IMPORT_COMPONENTS = Object.freeze({
  icon: RefreshIcon,
  content: ImportContent
})

const NOTIFICATION_CONTENT_BY_TYPE = Object.freeze({
  [NOTIFICATION_TYPE.CSV_FILE_PEOPLE_EXPORT]: FILE_EXPORT_COMPONENTS,
  [NOTIFICATION_TYPE.PPT_FILE_ORG_CHART_EXPORT]: FILE_EXPORT_COMPONENTS,
  [NOTIFICATION_TYPE.COMMENT]: COMMENT_COMPONENTS,
  [NOTIFICATION_TYPE.IMPORT]: IMPORT_COMPONENTS
})

const VARIANT_CLASSES = {
  [NOTIFICATION_ITEM_VARIANT.list]: {
    item: 'hover:bg-gray-50',
    icon: 'border-gray-300 bg-gray-100 text-gray-500'
  },
  [NOTIFICATION_ITEM_VARIANT.toast]: {
    item: 'bg-success-50 pr-10 hover:bg-success-100',
    icon: 'border-success-300 bg-success-100 text-success-600'
  }
}

const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  variant: {
    type: String,
    default: NOTIFICATION_ITEM_VARIANT.list,
    validator: (value) => Object.values(NOTIFICATION_ITEM_VARIANT).includes(value)
  }
})

const isInProgress = computed(() => props.notification.status === NOTIFICATION_STATUS.IN_PROGRESS)
const isDownloading = ref(false)

/**
 * Runs the notification's click action, ignoring clicks while a download is in flight.
 */
async function handleClick() {
  if (isDownloading.value) {
    return
  }

  isDownloading.value = true
  try {
    await props.notification.onClick()
  } finally {
    isDownloading.value = false
  }
}
const typeComponents = NOTIFICATION_CONTENT_BY_TYPE[props.notification.notificationType]

const { item: itemClasses, icon: iconColor } = VARIANT_CLASSES[props.variant]
</script>
